import { useCallback, useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { clearSiteContent } from "../../lib/siteContent";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ExternalLink, Plus, RotateCcw, Save, Trash2 } from "lucide-react";

const PAGES = [["about", "About", "/about"], ["privacy", "Privacy Policy", "/privacy"], ["trust", "Trust & Safety", "/trust-safety"]];
const LABELS = {
  badge: "Small badge above the title", title: "Page title", updated: "\"Last updated\" line", intro: "Intro paragraph",
  contact: "Closing line before the contact email", questions: "Closing line before the contact email",
  signs_title: "Warning signs heading", report_title: "Report box heading",
  report_p_1: "Report box text (before the email)", report_p_2: "Report box text (after the email)",
};
const SEC_NAME = { about: "Sections", privacy: "Sections", trust: "Tip cards" };
const blank = () => ({ en: "", fr: "" });

const BiField = ({ value, onChange, area, testid }) => (
  <div className="grid md:grid-cols-2 gap-3">
    {["en", "fr"].map((l) => {
      const C = area ? Textarea : Input;
      return (
        <label key={l} className="block text-xs text-slate-500">{l === "en" ? "English" : "French"}
          <C value={value?.[l] || ""} rows={area ? 3 : undefined} onChange={(e) => onChange({ ...value, [l]: e.target.value })} className="mt-1" data-testid={`${testid}-${l}`} />
        </label>
      );
    })}
  </div>
);

const ItemTools = ({ i, n, move, remove, testid }) => (
  <div className="flex gap-1">
    <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`${testid}-up`}><ArrowUp size={15} /></Button>
    <Button size="icon" variant="ghost" disabled={i === n - 1} onClick={() => move(i, 1)} data-testid={`${testid}-down`}><ArrowDown size={15} /></Button>
    <Button size="icon" variant="ghost" onClick={() => remove(i)} className="text-red-600" data-testid={`${testid}-remove`}><Trash2 size={15} /></Button>
  </div>
);

const moved = (arr, i, d) => { const a = [...arr]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; };

export function InfoPagesSettings() {
  const [slug, setSlug] = useState("about");
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setP(null); api.get(`/admin/site/pages/${slug}`).then((r) => setP(r.data.page)).catch((e) => toast.error(errText(e))); }, [slug]);
  useEffect(() => { load(); }, [load]);

  const done = (r, msg) => { setP(r.data.fields ? { ...p, ...r.data } : r.data); clearSiteContent(`page_${slug}`); toast.success(msg); };
  const save = () => { setBusy(true); api.put(`/admin/site/pages/${slug}`, p).then((r) => done(r, "Page saved")).catch((e) => toast.error(errText(e))).finally(() => setBusy(false)); };
  const reset = () => window.confirm("Put this page back to the original wording? Your edits will be lost.") &&
    api.delete(`/admin/site/pages/${slug}`).then((r) => { setP(r.data); clearSiteContent(`page_${slug}`); toast.success("Original wording restored"); });
  const setSec = (sections) => setP({ ...p, sections });
  const setList = (list) => setP({ ...p, list });

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6" data-testid="info-pages-settings">
      <div className="flex items-center gap-2 flex-wrap">
        {PAGES.map(([id, label]) => (
          <button key={id} onClick={() => setSlug(id)} data-testid={`info-page-${id}`}
            className={`px-4 h-9 rounded-xl border text-sm font-medium transition-colors ${slug === id ? "border-[#F5551E] bg-[#FFF4F0] text-[#F5551E]" : "border-slate-200 text-[#12234A] hover:border-[#F5551E]/50"}`}>{label}</button>
        ))}
        <a href={PAGES.find((x) => x[0] === slug)[2]} target="_blank" rel="noreferrer" className="ml-auto text-sm text-[#F5551E] inline-flex items-center gap-1" data-testid="info-page-view">View page <ExternalLink size={14} /></a>
      </div>
      {!p ? <p className="text-sm text-slate-400">Loading…</p> : (
        <>
          <p className="text-xs text-slate-500">Blank French fields show the English text. The contact email comes from Site Email Addresses{slug === "privacy" ? " — type {email} inside a section to show it there too" : ""}.</p>
          {Object.keys(p.fields).map((k) => (
            <div key={k} className="space-y-1.5">
              <p className="text-sm font-semibold text-[#12234A]">{LABELS[k] || k}</p>
              <BiField value={p.fields[k]} area={k === "intro"} onChange={(v) => setP({ ...p, fields: { ...p.fields, [k]: v } })} testid={`info-field-${k}`} />
            </div>
          ))}
          <div className="space-y-4">
            <h4 className="font-display font-bold text-[#12234A]">{SEC_NAME[slug]}</h4>
            {p.sections.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-2" data-testid={`info-sec-${i}`}>
                <div className="flex items-center justify-between"><span className="text-xs font-bold text-slate-400 uppercase">#{i + 1}</span>
                  <ItemTools i={i} n={p.sections.length} move={(j, d) => setSec(moved(p.sections, j, d))} remove={(j) => setSec(p.sections.filter((_, x) => x !== j))} testid={`info-sec-${i}`} /></div>
                <BiField value={s.h} onChange={(h) => setSec(p.sections.map((x, j) => (j === i ? { ...x, h } : x)))} testid={`info-sec-${i}-h`} />
                <BiField value={s.p} area onChange={(v) => setSec(p.sections.map((x, j) => (j === i ? { ...x, p: v } : x)))} testid={`info-sec-${i}-p`} />
              </div>
            ))}
            <Button variant="outline" onClick={() => setSec([...p.sections, { h: blank(), p: blank() }])} data-testid="info-sec-add" className="gap-1.5"><Plus size={15} /> Add {slug === "trust" ? "tip card" : "section"}</Button>
          </div>
          {p.list && (
            <div className="space-y-3">
              <h4 className="font-display font-bold text-[#12234A]">Warning signs list</h4>
              {p.list.map((s, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`info-list-${i}`}>
                  <div className="flex-1"><BiField value={s} onChange={(v) => setList(p.list.map((x, j) => (j === i ? v : x)))} testid={`info-list-${i}`} /></div>
                  <div className="pt-5"><ItemTools i={i} n={p.list.length} move={(j, d) => setList(moved(p.list, j, d))} remove={(j) => setList(p.list.filter((_, x) => x !== j))} testid={`info-list-${i}`} /></div>
                </div>
              ))}
              <Button variant="outline" onClick={() => setList([...p.list, blank()])} data-testid="info-list-add" className="gap-1.5"><Plus size={15} /> Add warning sign</Button>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={reset} data-testid="info-page-reset" className="gap-1.5"><RotateCcw size={15} /> Restore original</Button>
            <Button onClick={save} disabled={busy} data-testid="info-page-save" className="bg-[#F5551E] hover:bg-[#e04a15] gap-1.5"><Save size={15} /> Save page</Button>
          </div>
        </>
      )}
    </div>
  );
}
